import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, Search, MapPin, X } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { viewAllCity } from "../../Redux/Actions/settingsActions";
import "./cityDropDown.css";

const CityDropdown = ({ value, onChange }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    const { citiesList = { data: [] } } = useSelector((state) => state.city || {});
    const hasFetchedCities = useRef(false);

    useEffect(() => {
        if (!citiesList?.data?.length && !hasFetchedCities.current) {
            dispatch(viewAllCity());
            hasFetchedCities.current = true;
        }
    }, [dispatch, citiesList?.data?.length]);

    const options = useMemo(() => {
        return [
            ...citiesList.data.map(city => ({
                value: city._id,
                label: city.city_name
            }))
        ];
    }, [citiesList.data]);


    const filteredOptions = useMemo(() => {
        return options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);


    const selectedOption = useMemo(() => {
        return options.find(opt => opt.value === value);
    }, [options, value]);


    const handleSelect = (optionValue) => {
        onChange({ target: { value: optionValue } });
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange({ target: { value: "" } });
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleEvents = (event) => {
            if (event.type === "mousedown") {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                    setSearchTerm("");
                }
            }

            if (event.type === "keydown" && event.key === "Escape") {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleEvents);
        document.addEventListener("keydown", handleEvents);

        setTimeout(() => searchInputRef.current?.focus(), 100);

        return () => {
            document.removeEventListener("mousedown", handleEvents);
            document.removeEventListener("keydown", handleEvents);
        };
    }, [isOpen]);


    return (
        <>
            {isOpen && <div className="dropdown-backdrop" />}

            <div className="city-dropdown-container" ref={dropdownRef}>
                <button
                    className="city-dropdown-trigger"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-label="Select city"
                    type="button"
                >
                    <MapPin size={20} className="trigger-icon" />
                    <span className="trigger-label">
                        {selectedOption?.label || "Select City"}
                    </span>
                    {value && (
                        <X
                            size={16}
                            className="clear-icon"
                            onClick={handleClear}
                        />
                    )}
                    <ChevronDown
                        size={20}
                        className={`trigger-chevron ${isOpen ? "open" : ""}`}
                    />
                </button>

                {isOpen && (
                    <div className="city-dropdown-popup">
                        {/* Search header */}
                        <div className="popup-header">
                            <h3 className="popup-title">Select City</h3>
                            <button
                                className="popup-close"
                                onClick={() => setIsOpen(false)}
                                aria-label="Close dropdown"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="search-container">
                            <Search size={18} className="search-icon" style={{ top: "18px", left: "20px" }} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="search-input"
                                placeholder="Search cities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    className="search-clear"
                                    onClick={() => setSearchTerm("")}
                                    aria-label="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="options-list">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        className={`option-item ${value === option.value ? "selected" : ""
                                            }`}
                                        onClick={() => handleSelect(option.value)}
                                    >
                                        <MapPin size={16} className="option-icon" />
                                        <span className="option-text">{option.label}</span>
                                        {value === option.value && (
                                            <span className="option-check">✓</span>
                                        )}
                                    </button>
                                ))
                            ) : (
                                <div className="no-results">
                                    <MapPin size={40} className="no-results-icon" />
                                    <p className="no-results-text">No cities found</p>
                                    <p className="no-results-hint">Try a different search term</p>
                                </div>
                            )}
                        </div>

                        {/* Footer info */}
                        <div className="popup-footer">
                            <span className="footer-text">
                                {filteredOptions.length} {filteredOptions.length === 1 ? 'city' : 'cities'} available
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CityDropdown;